/**
 * ResellerLayout.jsx — pannello dedicato Reseller
 *
 * Menu semplificato (no DNS, no stats globali, no maintenance, no admin config):
 *   - Dashboard (welcome + counter utenti)
 *   - I miei utenti (CRUD scoped, NESSUN accesso a utenti di altri reseller)
 *   - Profilo (cambio password)
 *   - Esci
 *
 * Tutti gli endpoint sono /api/reseller/* → backend filtra per owner_id.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, LogOut, Plus, Edit, Trash2,
  Search, X, Check, AlertTriangle, Store, KeyRound, User,
  Activity, Eye, EyeOff, MessageSquare, Send
} from "lucide-react";
import { Menu } from "lucide-react";

const BACKEND_URL = (typeof window !== 'undefined' && process.env.NODE_ENV === 'production')
  ? window.location.origin
  : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${BACKEND_URL}/api`;

export default function ResellerLayout({ logout: logoutProp } = {}) {
  const [tab, setTab] = useState("dashboard");
  const [menuOpen, setMenuOpen] = useState(false);
  const selectTab = (t) => { setTab(t); setMenuOpen(false); };
  const [me, setMe] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/reseller/me`).then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const handleLogout = () => {
    if (logoutProp) logoutProp();
    else {
      // Fallback: clear localStorage manualmente
      localStorage.removeItem("sktv_admin_token");
      localStorage.removeItem("sktv_admin_refresh");
      localStorage.removeItem("sktv_role");
    }
    navigate("/login");
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header mobile con hamburger */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 bg-zinc-950 border-b border-zinc-800 flex items-center gap-3 px-4 py-3">
        <button
          data-testid="reseller-mobile-menu-btn"
          onClick={() => setMenuOpen(true)}
          className="text-zinc-300 hover:text-white p-1"
        >
          <Menu className="w-6 h-6" />
        </button>
        <img src="/logo.png" alt="SK TV" className="h-7 w-auto" />
        <span className="font-headings text-2xl text-netflix-red tracking-wider">SK TV</span>
      </header>
      {menuOpen && (
        <div className="md:hidden fixed inset-0 bg-black/70 z-40" onClick={() => setMenuOpen(false)} />
      )}
      {/* Sidebar */}
      <aside className={`w-64 bg-zinc-950 border-r border-zinc-800 h-screen fixed left-0 top-0 flex flex-col z-50 transition-transform duration-200 md:translate-x-0 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-6 border-b border-zinc-800 relative">
          <button
            data-testid="reseller-sidebar-close-btn"
            onClick={() => setMenuOpen(false)}
            className="md:hidden absolute top-4 right-4 text-zinc-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="SK TV" className="h-10 w-auto" />
            <div>
              <h1 className="font-headings text-3xl text-netflix-red tracking-wider">SK TV</h1>
              <p className="text-zinc-500 text-xs">Pannello Reseller</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <NavItem id="dashboard" current={tab} onClick={selectTab} icon={LayoutDashboard} label="Dashboard" />
          <NavItem id="users" current={tab} onClick={selectTab} icon={Users} label="I miei utenti" />
          <NavItem id="messages" current={tab} onClick={selectTab} icon={MessageSquare} label="Messaggi" />
          <NavItem id="profile" current={tab} onClick={selectTab} icon={User} label="Profilo" />
        </nav>

        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-netflix-red rounded-full flex items-center justify-center">
              <Store className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="font-medium truncate">{me?.username || "—"}</p>
              <p className="text-xs text-zinc-500">Reseller</p>
            </div>
          </div>
          <button
            data-testid="reseller-logout"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-zinc-900 hover:bg-zinc-800 rounded-sm text-zinc-400 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </aside>

      <main className="md:ml-64 p-4 pt-[70px] md:p-8 md:pt-8">
        {tab === "dashboard" && <ResellerDashboard me={me} onGoToUsers={() => setTab("users")} />}
        {tab === "users" && <ResellerUsersPage />}
        {tab === "messages" && <ResellerMessagesPage />}
        {tab === "profile" && <ResellerProfilePage me={me} onUpdated={() => {
          axios.get(`${API}/reseller/me`).then((r) => setMe(r.data)).catch(() => {});
        }} />}
      </main>
    </div>
  );
}

function NavItem({ id, current, onClick, icon: Icon, label }) {
  return (
    <button
      data-testid={`reseller-nav-${id}`}
      onClick={() => onClick(id)}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all
        ${current === id
          ? "bg-netflix-red/20 text-netflix-red border-l-4 border-netflix-red"
          : "text-zinc-400 hover:text-white hover:bg-zinc-900"}`}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium">{label}</span>
    </button>
  );
}

// ============================================================================
// DASHBOARD
// ============================================================================
function ResellerDashboard({ me, onGoToUsers }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reseller/users`, { params: { limit: 1 } })
      .then((r) => setStats({ total: r.data.total }))
      .catch(() => setStats({ total: 0 }));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Benvenuto, <span className="text-netflix-red">{me?.full_name || me?.username || "Reseller"}</span></h1>
        <p className="text-zinc-400 mt-1 text-sm">Ecco un riepilogo del tuo account.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card icon={Users} label="Utenti totali" value={me?.users_count ?? "—"} sub={`${me?.active_users_count ?? 0} attivi`} onClick={onGoToUsers} />
        <Card icon={Activity} label="Soglia scadenza" value={`${me?.expiry_threshold_days ?? 7} gg`} sub="prima del rinnovo" />
        <Card icon={Store} label="Limite max" value={me?.max_users ?? "∞"} sub="utenti consentiti" />
      </div>

      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6">
        <h2 className="text-lg font-bold mb-3">Stato account</h2>
        <Row label="Username" value={me?.username} />
        <Row label="Email" value={me?.email || "—"} />
        <Row label="Stato" value={me?.is_active ? "● ATTIVO" : "● SOSPESO"}
             valueClass={me?.is_active ? "text-green-500 font-bold" : "text-amber-500 font-bold"} />
        <Row label="Creato il" value={me?.created_at?.slice(0, 19).replace("T", " ") || "—"} />
        <Row label="Ultimo accesso" value={me?.last_login?.slice(0, 19).replace("T", " ") || "—"} />
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, sub, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`bg-zinc-950 border border-zinc-800 rounded-sm p-5 ${onClick ? "cursor-pointer hover:border-netflix-red/40" : ""}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-zinc-500 uppercase tracking-wider">{label}</span>
        <Icon className="w-4 h-4 text-netflix-red" />
      </div>
      <div className="text-3xl font-bold">{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{sub}</div>
    </div>
  );
}

function Row({ label, value, valueClass = "text-zinc-200" }) {
  return (
    <div className="flex justify-between py-2 border-b border-zinc-800/50 last:border-0">
      <span className="text-zinc-500 text-sm">{label}</span>
      <span className={`text-sm ${valueClass}`}>{value}</span>
    </div>
  );
}

// ============================================================================
// USERS CRUD (scoped)
// ============================================================================
function ResellerUsersPage() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dnsList, setDnsList] = useState([]);
  const [toast, setToast] = useState(null);
  const [showXtreamPwd, setShowXtreamPwd] = useState(false);

  const emptyForm = {
    username: "", password: "",
    xtream_username: "", xtream_password: "",
    dns_id: "", expiration_date: "",
    accessMode: "username", device_code: ""
  };
  const [createForm, setCreateForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reseller/users`, {
        params: search ? { search } : {}
      });
      setData(res.data);
    } catch (e) {
      setToast({ type: "error", msg: "Errore nel caricamento utenti" });
    }
    setLoading(false);
  }, [search]);

  const fetchDns = async () => {
    try {
      // Endpoint dedicato reseller (sola lettura)
      const res = await axios.get(`${API}/reseller/dns`);
      setDnsList(res.data || []);
    } catch (e) {
      setDnsList([]);
    }
  };

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchDns(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/reseller/users`, {
        ...createForm,
        label: (createForm.client_label || "").trim() || null,
        expiration_date: createForm.expiration_date || null
      });
      setToast({ type: "success", msg: "Utente creato" });
      setShowCreate(false);
      setCreateForm(emptyForm);
      fetchUsers();
    } catch (err) {
      setToast({ type: "error", msg: err.response?.data?.detail || "Errore" });
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      username: u.username,
      password: "",
      xtream_username: "",
      xtream_password: "",
      dns_id: u.dns_id || "",
      is_active: u.is_active,
      expiration_date: u.expiration_date?.slice(0, 10) || "",
      label: u.label || ""
    });
    setShowEdit(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      if (editForm.username !== editing.username) payload.username = editForm.username;
      if (editForm.password) payload.password = editForm.password;
      if (editForm.xtream_username) payload.xtream_username = editForm.xtream_username;
      if (editForm.xtream_password) payload.xtream_password = editForm.xtream_password;
      if (editForm.dns_id !== editing.dns_id) payload.dns_id = editForm.dns_id;
      if (editForm.is_active !== editing.is_active) payload.is_active = editForm.is_active;
      if ((editForm.label || "") !== (editing.label || "")) payload.label = editForm.label || "";
      const newExp = editForm.expiration_date || null;
      const oldExp = editing.expiration_date?.slice(0, 10) || null;
      if (newExp !== oldExp) payload.expiration_date = newExp ? `${newExp}T23:59:59+00:00` : null;
      await axios.put(`${API}/reseller/users/${editing.id}`, payload);
      setToast({ type: "success", msg: "Utente aggiornato" });
      setShowEdit(false);
      fetchUsers();
    } catch (err) {
      setToast({ type: "error", msg: err.response?.data?.detail || "Errore" });
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Eliminare l'utente "${u.username}"?`)) return;
    try {
      await axios.delete(`${API}/reseller/users/${u.id}`);
      setToast({ type: "success", msg: "Utente eliminato" });
      fetchUsers();
    } catch (err) {
      setToast({ type: "error", msg: err.response?.data?.detail || "Errore" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Users className="text-netflix-red" />
            I miei utenti
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">{data.total} utenti totali</p>
        </div>
        <button
          data-testid="reseller-add-user"
          onClick={() => setShowCreate(true)}
          className="bg-netflix-red hover:bg-red-700 px-5 py-2 rounded-sm font-bold flex items-center gap-2"
        >
          <Plus className="w-5 h-5" /> Nuovo utente
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          type="text"
          placeholder="Cerca utente…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-800 rounded-sm focus:border-netflix-red outline-none text-sm"
        />
      </div>

      <div className="bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Username</th>
              <th className="text-left px-4 py-3">DNS</th>
              <th className="text-left px-4 py-3">Devices</th>
              <th className="text-left px-4 py-3">Scadenza</th>
              <th className="text-left px-4 py-3">Stato</th>
              <th className="text-right px-4 py-3">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr><td colSpan="6" className="text-center py-8 text-zinc-500">Caricamento…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-8 text-zinc-500">
                Nessun utente. Premi "Nuovo utente".
              </td></tr>
            ) : data.items.map((u) => (
              <tr key={u.id} className="hover:bg-zinc-900/50 transition-colors">
                <td className="px-4 py-3 font-mono text-sm font-semibold">
                  {u.username}
                  {u.label && <div className="text-xs text-zinc-400 font-sans font-normal mt-0.5">{u.label}</div>}
                </td>
                <td className="px-4 py-3 text-xs text-zinc-400">{u.dns_name || "—"}</td>
                <td className="px-4 py-3 text-sm">{u.devices_count}/{u.max_devices}</td>
                <td className="px-4 py-3 text-xs">{u.expiration_date?.slice(0, 10) || "—"}</td>
                <td className="px-4 py-3">
                  {u.is_active ? (
                    <span className="text-green-500 text-xs font-bold">● ATTIVO</span>
                  ) : (
                    <span className="text-zinc-500 text-xs font-bold">● INATTIVO</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button
                      data-testid={`reseller-edit-user-${u.username}`}
                      onClick={() => openEdit(u)}
                      className="p-2 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white"
                      title="Modifica"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`reseller-delete-user-${u.username}`}
                      onClick={() => handleDelete(u)}
                      className="p-2 hover:bg-red-900/40 rounded text-zinc-400 hover:text-netflix-red"
                      title="Elimina"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <Modal title="Nuovo utente" onClose={() => setShowCreate(false)}>
          <form onSubmit={handleCreate} className="space-y-4">
            {/* Modalità di accesso */}
            <div className="bg-zinc-800/40 border border-zinc-700 rounded p-3">
              <p className="text-xs text-zinc-400 mb-2">Modalità di accesso app</p>
              <div className="flex gap-2">
                <button type="button"
                  onClick={() => setCreateForm({ ...createForm, accessMode: 'username' })}
                  className={`flex-1 px-3 py-2 rounded-sm text-sm font-semibold transition-colors ${
                    (createForm.accessMode || 'username') === 'username' ? 'bg-netflix-red text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'
                  }`}>
                  Username + Password
                </button>
                <button type="button"
                  onClick={() => setCreateForm({ ...createForm, accessMode: 'code' })}
                  className={`flex-1 px-3 py-2 rounded-sm text-sm font-semibold transition-colors ${
                    createForm.accessMode === 'code' ? 'bg-netflix-red text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'
                  }`}>
                  Codice dispositivo
                </button>
              </div>
            </div>

            {createForm.accessMode === 'code' ? (
              <>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">
                    Codice dispositivo (fornito dall'app del cliente)
                  </label>
                  <input
                    type="text"
                    value={createForm.device_code || ''}
                    onChange={(e) => setCreateForm({ ...createForm, device_code: e.target.value.toUpperCase() })}
                    placeholder="SKTV-AB12-CD34"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-4 py-2 font-mono uppercase tracking-wider text-white focus:border-netflix-red outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">
                    Etichetta cliente (opzionale, mostrata nel pannello)
                  </label>
                  <input
                    type="text"
                    value={createForm.client_label || ''}
                    onChange={(e) => setCreateForm({ ...createForm, client_label: e.target.value })}
                    placeholder="Es. Mario Rossi - TV Salotto"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-4 py-2 text-white focus:border-netflix-red outline-none"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Se vuoto, useremo il codice come identificativo nel pannello.
                  </p>
                </div>
              </>
            ) : (
              <>
                <Input label="Username" required value={createForm.username}
                       onChange={(v) => setCreateForm({ ...createForm, username: v })} />
                <Input label="Password (min 8)" type="password" required value={createForm.password}
                       onChange={(v) => setCreateForm({ ...createForm, password: v })} />
                <Input label="Etichetta cliente (opzionale, es. Mario Rossi - TV Salotto)"
                       value={createForm.client_label || ''}
                       onChange={(v) => setCreateForm({ ...createForm, client_label: v })} />
              </>
            )}
            <Input label="Xtream username" required value={createForm.xtream_username}
                   onChange={(v) => setCreateForm({ ...createForm, xtream_username: v })} />
            <PasswordInput label="Xtream password" value={createForm.xtream_password}
                           onChange={(v) => setCreateForm({ ...createForm, xtream_password: v })}
                           visible={showXtreamPwd} setVisible={setShowXtreamPwd} required />
            <SelectDns label="DNS" value={createForm.dns_id}
                       onChange={(v) => setCreateForm({ ...createForm, dns_id: v })}
                       options={dnsList} required />
            <Input label="Scadenza (opzionale)" type="date" value={createForm.expiration_date}
                   onChange={(v) => setCreateForm({ ...createForm, expiration_date: v })} />
            <ModalButtons onCancel={() => setShowCreate(false)} submitText="Crea utente" />
          </form>
        </Modal>
      )}

      {showEdit && editing && (
        <Modal title={`Modifica ${editing.username}`} onClose={() => setShowEdit(false)}>
          <form onSubmit={handleEdit} className="space-y-4">
            <Input label="Username" required value={editForm.username}
                   onChange={(v) => setEditForm({ ...editForm, username: v })} />
            <Input label="Nuova password (vuoto = invariata)" type="password"
                   value={editForm.password}
                   onChange={(v) => setEditForm({ ...editForm, password: v })} />
            <Input label="Nuovo Xtream username (vuoto = invariato)"
                   value={editForm.xtream_username}
                   onChange={(v) => setEditForm({ ...editForm, xtream_username: v })} />
            <PasswordInput label="Nuova Xtream password (vuoto = invariata)"
                           value={editForm.xtream_password}
                           onChange={(v) => setEditForm({ ...editForm, xtream_password: v })}
                           visible={showXtreamPwd} setVisible={setShowXtreamPwd} />
            <SelectDns label="DNS" value={editForm.dns_id}
                       onChange={(v) => setEditForm({ ...editForm, dns_id: v })}
                       options={dnsList} required />
            <Input label="Scadenza" type="date" value={editForm.expiration_date}
                   onChange={(v) => setEditForm({ ...editForm, expiration_date: v })} />
            <Input label="Etichetta cliente (es. Mario Rossi - TV Salotto)" value={editForm.label || ""}
                   onChange={(v) => setEditForm({ ...editForm, label: v })} />
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={editForm.is_active}
                     onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} />
              <span>Account attivo</span>
            </label>

            {/* Device codes (login alternativo senza username/password) */}
            <DeviceCodesSection
              userId={editing.id}
              codes={editing.device_codes || []}
              onUpdate={(newCodes) => {
                setEditing({ ...editing, device_codes: newCodes });
                fetchUsers();
              }}
              setToast={setToast}
            />

            <ModalButtons onCancel={() => setShowEdit(false)} submitText="Salva modifiche" />
          </form>
        </Modal>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============================================================================
// MESSAGES (scoped: solo verso i propri utenti)
// ============================================================================
function ResellerMessagesPage() {
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", target: "all", user_id: "" });
  const [toast, setToast] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [msgRes, usrRes] = await Promise.all([
        axios.get(`${API}/reseller/messages`),
        axios.get(`${API}/reseller/users`, { params: { limit: 500 } }),
      ]);
      setMessages(msgRes.data || []);
      setUsers(usrRes.data?.items || []);
    } catch {
      setToast({ type: "error", msg: "Errore nel caricamento" });
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const send = async (e) => {
    e.preventDefault();
    try {
      const payload = { title: form.title, content: form.content };
      if (form.target === "single" && form.user_id) payload.user_id = form.user_id;
      await axios.post(`${API}/reseller/messages`, payload);
      setToast({ type: "success", msg: "Messaggio inviato" });
      setShowModal(false);
      setForm({ title: "", content: "", target: "all", user_id: "" });
      fetchAll();
    } catch (err) {
      setToast({ type: "error", msg: err.response?.data?.detail || "Errore" });
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo messaggio?")) return;
    try {
      await axios.delete(`${API}/reseller/messages/${id}`);
      setToast({ type: "success", msg: "Messaggio eliminato" });
      fetchAll();
    } catch (err) {
      setToast({ type: "error", msg: err.response?.data?.detail || "Errore" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <MessageSquare className="text-netflix-red" /> Messaggi ai tuoi utenti
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">
            {messages.length} messaggi inviati • {users.length} utenti raggiungibili
          </p>
        </div>
        <button
          data-testid="reseller-new-message"
          onClick={() => setShowModal(true)}
          className="bg-netflix-red hover:bg-red-700 px-5 py-2 rounded-sm font-bold flex items-center gap-2"
        >
          <Plus className="w-5 h-5" /> Nuovo messaggio
        </button>
      </div>

      <div className="bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-zinc-500">Caricamento…</div>
        ) : messages.length === 0 ? (
          <div className="p-12 text-center text-zinc-500">
            <MessageSquare className="w-16 h-16 mx-auto mb-3 opacity-40" />
            Nessun messaggio inviato. Premi "Nuovo messaggio" per iniziare.
          </div>
        ) : (
          <table className="w-full min-w-[700px]">
            <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Titolo</th>
                <th className="text-left px-4 py-3">Destinatario</th>
                <th className="text-left px-4 py-3">Inviato</th>
                <th className="text-left px-4 py-3">Letto da</th>
                <th className="text-right px-4 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {messages.map((m) => (
                <tr key={m.id} className="hover:bg-zinc-900/50">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-sm">{m.title}</div>
                    <div className="text-xs text-zinc-500 line-clamp-1 mt-0.5">{m.content}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {m.is_broadcast ? (
                      <span className="bg-blue-900/40 text-blue-300 px-2 py-0.5 rounded">
                        Tutti i miei utenti
                      </span>
                    ) : (
                      <span className="bg-purple-900/40 text-purple-300 px-2 py-0.5 rounded">
                        {m.user_ids?.length > 1 ? `${m.user_ids.length} utenti` : "Singolo utente"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400">
                    {m.created_at?.slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="px-4 py-3 text-xs">{(m.read_by || []).length}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      data-testid={`reseller-delete-msg-${m.id}`}
                      onClick={() => remove(m.id)}
                      className="p-2 hover:bg-red-900/40 rounded text-zinc-400 hover:text-netflix-red"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <Modal title="Nuovo messaggio" onClose={() => setShowModal(false)}>
          <form onSubmit={send} className="space-y-4">
            <Input label="Titolo" required value={form.title}
                   onChange={(v) => setForm({ ...form, title: v })} />
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Contenuto</label>
              <textarea
                required rows={4}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-3 py-2 text-white focus:border-netflix-red outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Destinatario</label>
              <div className="flex gap-2">
                <button type="button"
                        onClick={() => setForm({ ...form, target: "all", user_id: "" })}
                        className={`flex-1 px-3 py-2 rounded-sm text-sm transition-colors ${
                          form.target === "all" ? "bg-netflix-red font-bold" : "bg-zinc-800 hover:bg-zinc-700"}`}>
                  Tutti i miei utenti
                </button>
                <button type="button"
                        onClick={() => setForm({ ...form, target: "single" })}
                        className={`flex-1 px-3 py-2 rounded-sm text-sm transition-colors ${
                          form.target === "single" ? "bg-netflix-red font-bold" : "bg-zinc-800 hover:bg-zinc-700"}`}>
                  Singolo utente
                </button>
              </div>
            </div>
            {form.target === "single" && (
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Seleziona utente</label>
                <select
                  required value={form.user_id}
                  onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-3 py-2 text-white focus:border-netflix-red outline-none text-sm"
                >
                  <option value="">— Seleziona —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.username}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={() => setShowModal(false)}
                      className="flex-1 bg-zinc-800 hover:bg-zinc-700 rounded-sm px-4 py-2">
                Annulla
              </button>
              <button type="submit"
                      className="flex-1 bg-netflix-red hover:bg-red-700 font-bold rounded-sm px-4 py-2 flex items-center justify-center gap-2">
                <Send className="w-4 h-4" /> Invia
              </button>
            </div>
          </form>
        </Modal>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============================================================================
// PROFILE
// ============================================================================
function ResellerProfilePage({ me, onUpdated }) {
  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [vpnPopup, setVpnPopup] = useState(me?.vpn_popup_enabled !== false);

  useEffect(() => {
    setVpnPopup(me?.vpn_popup_enabled !== false);
  }, [me?.vpn_popup_enabled]);

  const toggleVpnPopup = async () => {
    const enabled = !vpnPopup;
    try {
      await axios.patch(`${API}/reseller/me/vpn-popup`, { enabled });
      setVpnPopup(enabled);
      setToast({ type: "success", msg: enabled ? "Avviso VPN attivato per i tuoi utenti" : "Avviso VPN disattivato per i tuoi utenti" });
      if (onUpdated) onUpdated();
    } catch (err) {
      setToast({ type: "error", msg: err.response?.data?.detail || "Errore" });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) {
      setToast({ type: "error", msg: "Le due password non coincidono" });
      return;
    }
    if (pwd.length < 8) {
      setToast({ type: "error", msg: "La nuova password deve avere almeno 8 caratteri" });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/reseller/me/password`, {
        current_password: pwdCurrent,
        new_password: pwd
      });
      setToast({ type: "success", msg: "Password aggiornata con successo" });
      setPwdCurrent(""); setPwd(""); setPwd2("");
    } catch (err) {
      setToast({ type: "error", msg: err.response?.data?.detail || "Errore" });
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6 max-w-xl">
      <h1 className="text-3xl font-bold flex items-center gap-3">
        <User className="text-netflix-red" /> Profilo
      </h1>

      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 space-y-2">
        <Row label="Username" value={me?.username || "—"} />
        <Row label="Nome completo" value={me?.full_name || "—"} />
        <Row label="Email" value={me?.email || "—"} />
        <Row label="Soglia scadenza utenti" value={`${me?.expiry_threshold_days ?? 7} giorni`} />
        <Row label="Limite massimo utenti" value={me?.max_users ?? "Illimitati"} />
      </div>

      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 flex items-center justify-between gap-4" data-testid="reseller-vpn-popup-card">
        <div>
          <div className="font-bold">Avviso VPN all'avvio</div>
          <p className="text-sm text-zinc-400 mt-1">
            Se attivo, i tuoi utenti vedranno il popup di connessione VPN all'apertura dell'app.
          </p>
        </div>
        <button
          type="button"
          onClick={toggleVpnPopup}
          data-testid="reseller-vpn-popup-toggle"
          className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${vpnPopup ? "bg-netflix-red" : "bg-zinc-700"}`}
        >
          <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full transition-all ${vpnPopup ? "left-7" : "left-0.5"}`} />
        </button>
      </div>

      <form onSubmit={submit} className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 space-y-4">
        <h2 className="font-bold text-lg flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-netflix-red" /> Cambia password
        </h2>
        <Input label="Password corrente" type="password" required
               value={pwdCurrent} onChange={setPwdCurrent} />
        <Input label="Nuova password (min 8 caratteri)" type="password" required
               value={pwd} onChange={setPwd} />
        <Input label="Conferma nuova password" type="password" required
               value={pwd2} onChange={setPwd2} />
        <button type="submit" disabled={busy}
                data-testid="reseller-change-password"
                className="w-full bg-netflix-red hover:bg-red-700 disabled:opacity-50 rounded-sm py-2 font-bold transition-colors">
          {busy ? "Salvataggio…" : "Aggiorna password"}
        </button>
      </form>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================
function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-zinc-950 border border-zinc-800 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </motion.div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false }) {
  return (
    <div>
      <label className="block text-xs text-zinc-400 mb-1">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-3 py-2 text-white focus:border-netflix-red outline-none text-sm"
      />
    </div>
  );
}

function PasswordInput({ label, value, onChange, visible, setVisible, required = false }) {
  return (
    <div>
      <label className="block text-xs text-zinc-400 mb-1">{label}</label>
      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-3 py-2 pr-10 text-white focus:border-netflix-red outline-none text-sm"
        />
        <button type="button" onClick={() => setVisible(!visible)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white">
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function SelectDns({ label, value, onChange, options, required = false }) {
  return (
    <div>
      <label className="block text-xs text-zinc-400 mb-1">{label}</label>
      <select
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-3 py-2 text-white focus:border-netflix-red outline-none text-sm"
      >
        <option value="">— Seleziona DNS —</option>
        {options.map((d) => (
          <option key={d.id} value={d.id}>{d.name}</option>
        ))}
      </select>
      {options.length === 0 && (
        <p className="text-xs text-amber-500 mt-1">
          Nessun DNS disponibile. Contatta l'admin per assegnarti un DNS.
        </p>
      )}
    </div>
  );
}

function ModalButtons({ onCancel, submitText }) {
  return (
    <div className="flex gap-3 pt-4">
      <button type="button" onClick={onCancel}
              className="flex-1 bg-zinc-800 hover:bg-zinc-700 rounded-sm px-4 py-2 transition-colors">
        Annulla
      </button>
      <button type="submit"
              className="flex-1 bg-netflix-red hover:bg-red-700 font-bold rounded-sm px-4 py-2 transition-colors">
        {submitText}
      </button>
    </div>
  );
}

function DeviceCodesSection({ userId, codes, onUpdate, setToast }) {
  const [newCode, setNewCode] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const code = newCode.trim().toUpperCase();
    if (!code) return;
    if (!/^[A-Z0-9\-]{6,64}$/.test(code)) {
      setToast({ type: "error", msg: "Codice non valido (A-Z 0-9 '-' min 6 max 64)" });
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/reseller/users/${userId}/device-codes`, { device_code: code });
      onUpdate(res.data.device_codes);
      setNewCode("");
      setToast({ type: "success", msg: "Codice attivato" });
    } catch (err) {
      setToast({ type: "error", msg: err.response?.data?.detail || "Errore" });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Revocare il codice "${code}"?`)) return;
    setBusy(true);
    try {
      const res = await axios.delete(`${API}/reseller/users/${userId}/device-codes/${encodeURIComponent(code)}`);
      onUpdate(res.data.device_codes);
      setToast({ type: "success", msg: "Codice revocato" });
    } catch (err) {
      setToast({ type: "error", msg: err.response?.data?.detail || "Errore" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-zinc-800/40 border border-zinc-700 rounded p-3">
      <p className="text-xs uppercase tracking-wider text-zinc-400 mb-2">
        🔑 Codici dispositivo (login senza username/password)
      </p>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={newCode}
          onChange={(e) => setNewCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Es. SKTV-AB12-CD34"
          data-testid="reseller-device-code-input"
          disabled={busy}
          className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm font-mono uppercase tracking-wider text-white placeholder-zinc-500 focus:outline-none focus:border-netflix-red"
        />
        <button
          type="button"
          onClick={add}
          disabled={busy}
          data-testid="reseller-device-code-add-btn"
          className="bg-netflix-red hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded text-sm font-semibold transition-colors"
        >
          Attiva
        </button>
      </div>
      {(codes?.length ?? 0) === 0 ? (
        <p className="text-xs text-zinc-500 italic">Nessun codice attivo. L'utente vedrà il proprio codice in fondo alla schermata di login dell'app.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {codes.map((c) => (
            <span key={c} className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-700 rounded-full pl-3 pr-1 py-1 text-xs font-mono">
              {c}
              <button
                type="button"
                onClick={() => remove(c)}
                data-testid={`reseller-device-code-remove-${c}`}
                disabled={busy}
                className="text-zinc-500 hover:text-netflix-red rounded-full p-1 disabled:opacity-50"
                title="Revoca codice"
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Toast({ msg, type, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      className={`fixed bottom-4 right-4 px-6 py-3 rounded-sm shadow-lg z-50 flex items-center gap-3
        ${type === "success" ? "bg-green-600" : "bg-netflix-red"}`}
    >
      {type === "success" ? <Check className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
      <span className="font-medium">{msg}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-70"><X className="w-4 h-4" /></button>
    </motion.div>
  );
}
